/** Pendant zu `Client/Components/Base/IwmBaseForm.razor` — Basisklasse als Hook. */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface EntityFormOptions<T> {
  id: number;
  createEmpty(): T;
  fetch(id: number): Promise<T | null>;
  /** Clientseitige Prüfung; eine leere Liste bedeutet „gültig“. */
  validate?(model: T): string[];
}

export interface EntityForm<T> {
  model: T;
  setModel(updater: T | ((current: T) => T)): void;
  setField<K extends keyof T>(key: K, value: T[K]): void;
  saving: boolean;
  setSaving(value: boolean): void;
  errors: string[];
  isValid: boolean;
  reload(): Promise<void>;
  reset(): void;
}

/**
 * Verhalten von `IwmBaseForm`:
 *
 * - `id > 0` → beim Einhängen und bei jeder Änderung von `id` wird `fetch(id)` geladen.
 * - `id === 0` → `createEmpty()`; ebenso, wenn `fetch` `null` liefert (`APIBase` meldet
 *   Fehler durch `null`).
 * - `isValid` ersetzt `editContext.Validate()`.
 */
export function useEntityForm<T>(options: EntityFormOptions<T>): EntityForm<T> {
  const { id } = options;

  // Die Optionen werden bei jedem Rendern neu gebildet; der Ladevorgang greift deshalb
  // über eine Referenz auf den jeweils aktuellen Stand zu.
  const latest = useRef(options);
  latest.current = options;
  // Verwirft Antworten überholter Ladevorgänge.
  const generation = useRef(0);

  const [model, setModelState] = useState<T>(() => options.createEmpty());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (entryId: number): Promise<void> => {
    generation.current += 1;
    const current = generation.current;

    if (entryId <= 0) {
      setModelState(latest.current.createEmpty());
      return;
    }

    const loaded = await latest.current.fetch(entryId);
    if (generation.current !== current) return;
    setModelState(loaded ?? latest.current.createEmpty());
  }, []);

  useEffect(() => {
    void load(id);
  }, [id, load]);

  const setModel = useCallback((updater: T | ((current: T) => T)) => {
    setModelState((current) =>
      typeof updater === 'function' ? (updater as (value: T) => T)(current) : updater,
    );
  }, []);

  const setField = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setModelState((current) => ({ ...current, [key]: value }));
  }, []);

  const reload = useCallback(() => load(latest.current.id), [load]);

  const reset = useCallback(() => {
    setModelState(latest.current.createEmpty());
  }, []);

  // Pendant zu `editContext.Validate()`, das im Altclient bei jedem Zeichnen läuft.
  const errors = options.validate?.(model) ?? [];

  return {
    model,
    setModel,
    setField,
    saving,
    setSaving,
    errors,
    isValid: errors.length === 0,
    reload,
    reset,
  };
}
